import { verticalFadeIn } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import { baseGrid } from '@helpers/grid';
import { Device, from, until } from '@helpers/media';
import { h2, h3, paragraphLarge } from '@helpers/typography';
import SectionWrapper from '@stories/Components/Misc/SectionWrapper/SectionWrapper';
import SectionWrapperStyles from '@stories/Components/Misc/SectionWrapper/SectionWrapper.styles';
import { m } from 'framer-motion';
import styled, { css } from 'styled-components';
const MotionWrapper = styled(m.div).attrs((props) => ({
    initial: 'hidden',
    animate: props['data-in-view'] ? 'visible' : 'hidden',
    transition: {
        staggerChildren: 0.1,
    },
})) `
  display: contents;
`;
export const logoSizes = css `
  --width: ${fluid(135, 173)};
  --height: ${fluid(84, 108)};
`;
const Container = styled(SectionWrapper) `
  ${logoSizes};

  &[data-variant='blue'] {
    --titleHighlight: ${brand.secondary.ice};
    --textColour: ${brand.primary.white};

    background: linear-gradient(104.72deg, #3372aa 0%, #1e2f6d 100%);
  }

  &[data-variant='white'] {
    --textColour: ${brand.primary.black};
  }

  overflow: hidden;

  @media ${from(Device.TabletLarge)} {
    &[data-use-reduced-layout='false'] {
      ${SectionWrapperStyles.Inner} {
        padding-top: calc(var(--sectionSpacingBottom) * 0.66);
        padding-bottom: calc(var(--sectionSpacingBottom) * 0.75);
      }
    }
  }
`;
const Inner = styled.div `
  @media ${until(Device.TabletLarge)} {
    display: flex;
    flex-direction: column;
  }

  @media ${from(Device.TabletLarge)} {
    [data-use-reduced-layout='false'] & {
      ${baseGrid}
      align-items: center;
      min-height: calc(980px - (var(--sitePadding) * 2));
    }

    [data-use-reduced-layout='true'] & {
      display: flex;
      flex-direction: column;
    }
  }
`;
const ContentSection = styled.div `
  display: flex;
  flex-direction: column;
  grid-column: 8 / -1;

  @media ${until(Device.TabletLarge)} {
    order: -1;
    margin-bottom: 35px;
  }
`;
const Title = styled.h2 `
  ${h2};

  color: var(--textColour) !important;
  margin-bottom: 0 !important;
  line-height: 1.25 !important; // ensure that the text doesn't get cut off within highlight

  [data-use-reduced-layout='true'] & {
    ${h3};
  }

  @media ${from(Device.TabletLarge)} {
    [data-use-reduced-layout='true'] & {
      text-align: center;
    }
  }

  span {
    background: var(--titleHighlight, currentColor);
    background-clip: text;
    color: transparent;
    display: inline;
  }
`;
const Content = styled.div `
  ${paragraphLarge};

  color: var(--textColour);
  margin: ${fluid(16, 24)} 0 0;

  @media ${from(Device.TabletLarge)} {
    [data-use-reduced-layout='true'] & {
      text-align: center;
    }
  }
`;
const CTAsContainer = styled.div `
  display: flex;
  gap: ${fluid(8, 24)};
  margin-top: ${fluid(32, 40)};

  @media ${from(Device.TabletLarge)} {
    align-items: center;
    [data-use-reduced-layout='true'] & {
      justify-content: center;
    }
  }

  @media ${until(Device.TabletLarge)} {
    flex-direction: column;
    width: max-content;
    max-width: 100%;
  }
`;
const LogoGridWrapper = styled(m.div).attrs(() => ({
    variants: {
        visible: {
            transition: { staggerChildren: 0.025 },
        },
    },
})) `
  grid-column: 1 / span 6;
  transform: rotate(-8deg);

  @media ${from(Device.TabletLarge)} {
    transform: rotate(-8deg) translateX(-50px);
  }

  @media ${until(Device.TabletLarge)} {
    max-height: 320px;
  }
`;
const LogoGridCompact = styled.div `
  --gap: ${fluid(10, 26)};

  order: 1;
  display: flex;
  gap: var(--gap);
  margin-top: ${fluid(11, 70)};
  left: 0;
  position: relative;
  animation: scroll calc(var(--count) * 1.15s) linear infinite;

  @keyframes scroll {
    0% {
      transform: translateX(calc((var(--width) + var(--gap)) * (var(--count) * -1)));
    }

    100% {
      transform: translateX(calc((var(--width) + var(--gap)) * (var(--count) * -2)));
    }
  }
`;
const LogoGridInner = styled.div `
  --offset: calc(var(--gridColumnWidth) * 2 + var(--gutterWidth));

  width: calc(100% + var(--offset));
  position: relative;
  left: calc(-1 * (var(--offset) / 2));
  display: flex;
  flex-direction: column;
  gap: ${fluid(8, 15)};

  @media ${from(Device.TabletLarge)} {
    left: calc(-1 * var(--offset));
  }
`;
const LogoGridRow = styled.div `
  display: flex;
  gap: ${fluid(8, 15)};
  justify-content: center;
`;
const Logo = styled(m.div).attrs(() => ({ variants: verticalFadeIn() })) `
  width: var(--width);
  min-width: var(--width);
  height: var(--height);
  min-height: var(--height);
  background-color: ${brand.primary.white};
  border-radius: ${fluid(25, 32)};
  padding: ${fluid(15, 20)};
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
`;
const LogoImage = styled(m.img).attrs((props) => ({
    initial: 'hidden',
    animate: props['data-loaded'] ? 'visible' : 'hidden',
    exit: 'hidden',
    variants: {
        hidden: {
            opacity: 0,
        },
        visible: {
            opacity: 1,
        },
    },
})) `
  width: 100%;
  height: 100%;
  object-fit: scale-down;
  object-position: center center;
  max-height: 35px;
`;
export const LogoListStyles = {
    Container,
    Inner,
    ContentSection,
    Title,
    Content,
    CTAsContainer,
    LogoGridWrapper,
    LogoGridInner,
    LogoGridRow,
    Logo,
    LogoImage,
    LogoGridCompact,
    MotionWrapper,
};
